import React from "react"
import styled, { css } from "styled-components"
import { up } from "styled-breakpoints"
import colors from "@constants/colors"
import { timings, cubicBezier } from "@constants/animation"
import { body, bodyBold } from "@constants/typography"
import { Chevron } from "@components/icons"

const Header = styled.div`
  ${body}
  flex-direction: row;
  align-items: center;
  align-self: flex-end;
  justify-content: flex-end;
  display: flex;
  transition: width ${timings.medium} ${cubicBezier};
  height: 44px;
  padding: 0 16px;
  border: solid 1px ${colors.divider};
  border-radius: 2px;
  background-color: ${colors.snow};

  ${up("laptop")} {
    &:hover {
      cursor: pointer;
    }
  }
`

const HelperText = styled.div`
  position: absolute;
  left: 16px;
  opacity: 0;
  transition: opacity ${timings.medium} ${cubicBezier};
  line-height: 44px;
  color: ${colors.slateDark};
  pointer-events: none;
`

const Value = styled.div`
  opacity: 1;
  transition: opacity ${timings.medium} ${cubicBezier};
  margin-right: 14px;
  line-height: 44px;
  color: ${colors.slateDark};
`

const IconWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  transform: rotate(0);
  transition: transform ${timings.medium} ${cubicBezier};
  width: 21px;
  height: 21px;
  pointer-events: none;
`

const Options = styled.div`
  opacity: 0;
  transition: opacity ${timings.medium} ${cubicBezier};
  width: 100%;
  border: solid 1px ${colors.divider};
  border-radius: 2px;
  background-color: ${colors.snow};
`

const Option = styled.div<{ selected: boolean }>`
  ${({ selected }) => css`
    ${selected ? bodyBold : body}
    transition: background-color ${timings.medium} ${cubicBezier};
    padding: 0 16px;
    line-height: 44px;
    cursor: pointer;
    color: ${colors.slateDark};
    background-color: ${colors.snow};

    &:hover {
      background-color: ${colors.divider};
    }
  `}
`

export const Container = styled.div`
  position: relative;
  flex-direction: column;
  align-items: center;
  display: flex;
  width: 100%;
  height: 44px;

  ${Options} {
    pointer-events: none;
  }

  :active,
  :focus {
    outline: none;
    overflow: visible;

    ${Header} {
      width: 100%;
      border-bottom: none;
    }

    ${HelperText} {
      width: 100%;
      height: 100%;
      opacity: 1;
      pointer-events: all;
    }

    ${Value} {
      opacity: 0;
    }

    ${IconWrapper} {
      transform: rotate(180deg);
    }

    ${Options} {
      opacity: 1;
      border-top: none;
      pointer-events: initial;
    }
  }
`

interface DropdownProps {
  helperText: string
  value: string
  options: Array<{ label: string; value: string }>
  onChange: (string) => void
}

const Dropdown: React.FC<DropdownProps> = ({
  helperText,
  value,
  options,
  onChange,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)

  const closeDropdown = () => {
    if (containerRef && containerRef.current) {
      containerRef.current.blur()
    }
  }

  const getHandlerForOption = (option) => {
    return React.useCallback(() => {
      onChange(option)
      closeDropdown()
    }, [option, onChange])
  }

  const optionForValue = options.find((option) => option.value === value)

  return (
    <Container ref={containerRef} tabIndex={0}>
      <Header>
        <HelperText onClick={closeDropdown}>{helperText}</HelperText>
        <Value>{optionForValue && optionForValue.label}</Value>
        <IconWrapper>
          <Chevron />
        </IconWrapper>
      </Header>
      <Options>
        {options.map((option) => (
          <Option
            key={option.label}
            selected={option.value === value}
            onClick={getHandlerForOption(option)}
          >
            {option.label}
          </Option>
        ))}
      </Options>
    </Container>
  )
}

export default Dropdown
